import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { SECTIONS, type NavItem, type NavSection } from './navigation';
import { Icons } from './icons';
import { usePermission } from '../../hooks/usePermission';

/**
 * The menu bar, in the shape every Indian business site uses: a short row of
 * headings in small capitals, divided by hairlines, each opening a list.
 *
 * Thirty-three screens will not sit in a row, so they are grouped by the job
 * being done — selling, savings, buying, stock, money, reports, setup — and
 * only nine words sit on the bar. Two of those are links rather than groups:
 * Home, and Rates, because the day's rate is checked more often than anything
 * else and should never be two clicks away.
 */
export function TopNav() {
  const { can } = usePermission();
  const location = useLocation();
  const navigate = useNavigate();
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  const sections = useMemo(
    () =>
      SECTIONS.map((s) => ({ ...s, items: s.items.filter((i) => !i.permission || can(i.permission)) })).filter(
        (s) => s.items.length > 0,
      ),
    [can],
  );

  // A section with `to` is a plain link; everything else opens a list.
  const groups = sections;

  // Click outside closes any open dropdown.
  useEffect(() => {
    const onClick = (e: MouseEvent): void => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) setOpenGroup(null);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => setOpenGroup(null), [location.pathname, location.search]);

  /** A heading on the bar: small capitals, the current one in the brand colour. */
  const headingClass = (current: boolean): string =>
    `flex items-center gap-1 whitespace-nowrap px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors ${
      current ? 'text-velvet' : 'text-ink hover:text-velvet'
    }`;

  /**
   * Whether a menu entry is the page being looked at. Some entries carry a
   * query — /reports?group=Sales — so the path alone is not enough, and a plain
   * /reports must not light up while a group is open.
   */
  const isCurrent = (to: string): boolean => {
    const [path, qs] = to.split('?');
    if (location.pathname !== path) return false;
    if (!qs) return location.search === '';
    // The entry's own query has to be there; anything else on the address
    // (an open document, say) does not stop it being the current page.
    const here = new URLSearchParams(location.search);
    return [...new URLSearchParams(qs)].every(([k, v]) => here.get(k) === v);
  };

  const groupHasCurrent = (section: NavSection): boolean => section.items.some((i) => isCurrent(i.to));

  return (
    <div ref={barRef} className="flex items-center">
      {groups.map((section, idx) => {
        const isOpen = openGroup === section.heading;
        const current = groupHasCurrent(section);

        // A hairline between headings, as on a printed masthead.
        const divider = idx > 0 ? <span className="h-3.5 w-px shrink-0 bg-silver-400/50" aria-hidden="true" /> : null;

        if (section.to) {
          return (
            <div key={section.heading} className="flex items-center">
              {divider}
              <NavLink to={section.to} end={section.to === '/'} className={({ isActive }) => headingClass(isActive)}>
                {section.heading}
              </NavLink>
            </div>
          );
        }

        return (
          <div key={section.heading} className="flex items-center">
            {divider}
            <div
              className="relative"
              onMouseEnter={() => setOpenGroup(section.heading)}
              onMouseLeave={() => setOpenGroup(null)}
            >
              <button
                className={headingClass(current)}
                onClick={() => setOpenGroup(isOpen ? null : section.heading)}
                aria-expanded={isOpen}
              >
                {section.heading}
                <svg viewBox="0 0 12 12" className="h-2 w-2 opacity-60" aria-hidden="true">
                  <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              {/* The current group keeps a mark under it, so you can see where you are. */}
              {current ? <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-gold-400" /> : null}

              {isOpen ? (
                <div className="absolute left-0 top-full z-30 w-60 overflow-hidden rounded-b-md border border-t-2 border-silver-100 border-t-velvet bg-white py-1 shadow-pop">
                  {section.items.map((item: NavItem) => {
                    const Icon = Icons[item.icon];
                    const active = isCurrent(item.to);
                    return (
                      <button
                        key={item.to}
                        className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                          active ? 'bg-velvet-50 font-medium text-velvet' : 'text-ink hover:bg-velvet-50'
                        }`}
                        onClick={() => navigate(item.to)}
                      >
                        <span className={active ? 'text-velvet' : 'text-ink-soft'}>
                          <Icon />
                        </span>
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
